import { getDatabase, ref, push } from 'firebase/database';
import { APP_VERSION, ENV, LOG_PATH } from '../constants';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

interface ErrorDetails {
  message?: string;
  name?: string;
  stack?: string;
  code?: string;
  [key: string]: any;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  app: 'admin' | 'public' | 'api';
  context?: LogContext;
  error?: ErrorDetails;
  version?: string;
}

/**
 * Rate limiting for public app - prevent excessive logging
 * Limits: Max 10 logs per minute per session
 */
class RateLimiter {
  private logs: number[] = [];
  private readonly MAX_LOGS_PER_MINUTE = 10;
  private readonly WINDOW_MS = 60 * 1000;

  canLog(): boolean {
    const now = Date.now();
    this.logs = this.logs.filter(timestamp => now - timestamp < this.WINDOW_MS);
    
    if (this.logs.length >= this.MAX_LOGS_PER_MINUTE) {
      return false;
    }
    
    this.logs.push(now);
    return true;
  }
}

const rateLimiter = new RateLimiter();

/**
 * Non-blocking Firebase logger - never throws, never blocks user operations
 * Fire-and-forget pattern: logs are sent asynchronously without awaiting
 * No authentication required for public app
 */
const logToFirebase = (entry: LogEntry): void => {
  Promise.resolve().then(async () => {
    try {
      if (entry.app === 'public' && !rateLimiter.canLog()) {
        if (entry.level !== 'error') {
          return;
        }
      }

      const DB = getDatabase();
      const logData = {
        level: entry.level,
        message: entry.message,
        timestamp: entry.timestamp,
        app: entry.app,
        version: entry.version || APP_VERSION,
        ...(entry.context && { context: entry.context }),
        ...(entry.error && { error: entry.error }),
      };

      await push(ref(DB, LOG_PATH), logData);
    } catch (error) {
      if (ENV === 'dev') {
        console.warn('[Firebase Logger] Failed to send log to Firebase:', error);
      }
    }
  });
};

/**
 * Rate limiting: Only log errors and warnings in production to reduce costs
 */
const shouldLogToFirebase = (level: LogLevel): boolean => {
  if (ENV === 'dev') return true;
  return level === 'warn' || level === 'error';
};

const firebaseLogger = {
  /**
   * Log debug messages (dev only)
   */
  debug: (message: string, context?: LogContext): void => {
    if (shouldLogToFirebase('debug')) {
      logToFirebase({
        level: 'debug',
        message,
        timestamp: new Date().toISOString(),
        app: 'public',
        context,
        version: APP_VERSION,
      });
    }
  },

  /**
   * Log info messages
   */
  info: (message: string, context?: LogContext): void => {
    if (shouldLogToFirebase('info')) {
      logToFirebase({
        level: 'info',
        message,
        timestamp: new Date().toISOString(),
        app: 'public',
        context,
        version: APP_VERSION,
      });
    }
  },

  /**
   * Log warning messages
   */
  warn: (message: string, context?: LogContext): void => {
    logToFirebase({
      level: 'warn',
      message,
        timestamp: new Date().toISOString(),
      app: 'public',
      context,
      version: APP_VERSION,
    });
  },

  /**
   * Log error messages (always logged, bypasses rate limit)
   */
  error: (message: string, error?: Error | unknown, context?: LogContext): void => {
    let errorDetails: ErrorDetails = {};
    if (error instanceof Error) {
      errorDetails = {
        message: error.message,
        name: error.name,
        stack: error.stack,
      };
    } else if (error && typeof error === 'object') {
      errorDetails = {
        code: (error as any).code || undefined,
        message: (error as any).message || String(error),
        name: (error as any).name || 'UnknownError',
        ...(error as any),
      };
    } else if (error) {
      errorDetails = { message: String(error) };
    }

    logToFirebase({
      level: 'error',
      message,
        timestamp: new Date().toISOString(),
      app: 'public',
      context,
      error: errorDetails,
      version: APP_VERSION,
    });
  },
};

export default firebaseLogger;
